import DirectionsBusIcon from '@mui/icons-material/DirectionsBus'
import MapIcon from '@mui/icons-material/Map'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import PlaceIcon from '@mui/icons-material/Place'
import {
    Box,
    CircularProgress,
    debounce,
    Dialog,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemButton,
    ListSubheader,
    TextField,
    Typography,
} from '@mui/material'
import { t } from 'i18next'
import { useContext, useEffect, useState } from 'react'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import { useSearchGeocodingSearchGet } from 'src/generated/brezavta/queries'
import { SearchResult } from 'src/generated/brezavta/requests'
import { getFavourites } from 'src/lib/pois'

interface SearchSelectProps {
    onSelect: (result: Result) => void
}

export const SearchSelect = ({ onSelect }: SearchSelectProps) => {
    const { map } = useContext(MapRenderContext)

    const [query, setQuery] = useState('')
    const [acquiringLocation, setAcquiringLocation] = useState(false)

    const { data } = useSearchGeocodingSearchGet({
        query: {
            string: query,
            focus_lat: map?.current ? map.current.getCenter().lat : undefined,
            focus_lon: map?.current ? map.current.getCenter().lng : undefined,
        },
    })

    const debouncedSetQuery = debounce((nextValue: string) => {
        setQuery(nextValue)
    }, 100)

    // Remap search results to rows

    const [results, setResults] = useState<Result[]>([])

    useEffect(() => {
        const newResults = (data || []).map(resultFromSearchResult)
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setResults(newResults)
    }, [data])

    // add event listner to input field
    const search = (event: React.ChangeEvent<HTMLInputElement>) => {
        debouncedSetQuery(event.target.value)
    }

    const recents = getRecents()
    const favourites = getFavourites()

    const handleMyLocationClick = () => {
        setAcquiringLocation(true)
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setAcquiringLocation(false)
                const result = {
                    id: 'my-location',
                    location: {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    },
                    title: t('My location'),
                } as Result
                onSelect(result)
            },
            (error) => {
                setAcquiringLocation(false)
                if (error.code === error.PERMISSION_DENIED) {
                    alert(t('Location permission denied. Please enable location services in your browser settings.'))
                }
                else {
                    alert(t('Error acquiring location: ') + error.message)
                }
            },
        )
    }

    return (
        <List style={{ padding: 0, width: '100%' }}>
            <ListItem>
                <TextField
                    style={{ marginTop: '5px' }}
                    label={(
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {t('Address, place or business name')}
                        </div>
                    )}
                    onChange={search}
                    variant="outlined"
                    fullWidth
                    autoFocus // Add autoFocus prop to make it active by default
                />
            </ListItem>

            {query && results.map((result) => {
                return <SearchItem key={result.id} result={result} onSelect={onSelect} />
            })}

            {(!query) && (
                <>
                    <SearchItemRow
                        icon={<MyLocationIcon />}
                        rowKey="my-location"
                        title={t('My location')}
                        subtitle={undefined}
                        onClick={handleMyLocationClick}
                    />
                    <Dialog
                        open={acquiringLocation}
                        aria-labelledby="loading-dialog-title"
                    >
                        <DialogTitle id="loading-dialog-title">{t('Acquiring location...')}</DialogTitle>
                        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </DialogContent>
                    </Dialog>
                </>

            )}

            {(!query && favourites.length != 0) && (
                <>
                    <ListSubheader>
                        {t('Favourites')}
                    </ListSubheader>
                    {favourites.map((poi) => {
                        const result: Result = {
                            id: poi.id,
                            title: poi.name,
                            location: { latitude: poi.lat, longitude: poi.lon },
                            type: 'POI',
                        }
                        return <SearchItem key={poi.id} result={result} onSelect={onSelect} />
                    })}
                </>
            )}

            {!query && (
                <>
                    <ListSubheader>
                        {t('Recent searches')}
                    </ListSubheader>
                    {recents.map((result) => {
                        return <SearchItem key={result.id} result={result} onSelect={onSelect} />
                    })}
                </>
            )}
        </List>
    )
}

export interface Result {
    id: string
    title: string
    subtitle?: string | null
    location: { latitude: number | null, longitude: number | null } | null
    type: string
    otp_code?: string
}

function resultFromSearchResult(searchResult: SearchResult): Result {
    return {
        id: searchResult.otp_code + '',
        title: searchResult.label,
        subtitle: searchResult.description,
        location: { latitude: searchResult.lat, longitude: searchResult.lon },
        type: searchResult.type,
    }
}

interface SearchItemProps {
    result: Result
    onSelect: (result: Result) => void
}

function SearchItem({ result, onSelect }: SearchItemProps) {
    return (
        <SearchItemRow
            icon={iconForResult(result)}
            rowKey={result.id}
            title={result.title}
            subtitle={result.subtitle}
            onClick={() => {
                updateRecent(result)
                onSelect(result)
            }}
        />
    )
}

interface SearchItemRowProps {
    rowKey: string
    icon: React.ReactNode
    title: string
    subtitle?: string | null
    onClick: () => void
}

function SearchItemRow({ rowKey, icon, title, subtitle, onClick }: SearchItemRowProps) {
    return (
        <ListItemButton
            key={rowKey}
            onClick={onClick}
        >
            <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                sx={{
                    width: '100%',
                    height: '3rem',
                    borderBottom: '1px solid #f0f0f0',
                }}
            >
                <Box
                    marginRight={1}
                    sx={{ color: 'primary.main' }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography variant="body1">{title}</Typography>
                    <Typography variant="body2" color="textSecondary">{subtitle}</Typography>
                </Box>
                <Box sx={{ marginLeft: 'auto' }}>
                    <NavigateNextIcon sx={{ color: 'primary.main' }} />
                </Box>
            </Box>
        </ListItemButton>
    )
}

function iconForResult(result: Result) {
    switch (result.type) {
        case 'geocode':
            return <MapIcon />
        case 'transit_stop':
            return <DirectionsBusIcon />
        default:
            return <PlaceIcon />
    }
}

export function getRecents(): Result[] {
    const storedResults = localStorage.getItem('recents') || '[]'
    return JSON.parse(storedResults)
}

export function updateRecent(result: Result): Result[] {
    const recents = getRecents()
    const index = recents.findIndex(r => r.title == result.title)
    if (index > -1) {
        recents.splice(index, 1)
    }
    recents.unshift(result)
    localStorage.setItem('recents', JSON.stringify(recents.slice(0, 10)))
    return recents.slice(0, 10)
}
